import { useRef } from 'react'

/** 폼 모달의 필드 사이를 ← → ↑ ↓ 로 이동시키는 공용 hook.
 *
 *  선형 순서(FIELDS 배열 순서)를 따르며:
 *   - text/number input: 커서가 시작/끝일 때만 ← / → 로 필드 이동 (커서 이동 우선)
 *   - select / date input: ↑ ↓ 는 native (옵션 변경/날짜 파트), ← → 는 필드 이동
 *   - 그 외 (button, checkbox 등): 모든 방향 필드 이동
 *
 * 사용:
 *   const FIELDS = ['name', 'biz_class', ...] as const
 *   const { formRef, onKeyDown, focusField } = useFormFieldNavigation(FIELDS)
 *   ...
 *   <form ref={formRef} onKeyDown={onKeyDown}>
 *     <input data-form-field="name" ... />
 *     <select data-form-field="biz_class" ... />
 *   </form>
 */
export function useFormFieldNavigation<F extends readonly string[]>(fields: F) {
  const formRef = useRef<HTMLFormElement>(null)

  function focusField(name: F[number]) {
    requestAnimationFrame(() => {
      const container = formRef.current?.querySelector<HTMLElement>(`[data-form-field="${name}"]`)
      if (!container) return
      const target = ['INPUT', 'SELECT', 'BUTTON', 'TEXTAREA'].includes(container.tagName)
        ? container
        : container.querySelector<HTMLElement>('input, select, button, textarea')
      if (!target) return
      target.focus()
      if (target instanceof HTMLInputElement && ['text', 'number', ''].includes(target.type)) target.select()
    })
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLFormElement>) {
    const el = e.target instanceof HTMLElement ? e.target : null
    if (!el) return
    const fieldEl = el.closest<HTMLElement>('[data-form-field]')
    const rawField = fieldEl?.getAttribute('data-form-field')
    if (!rawField || !(fields as readonly string[]).includes(rawField)) return
    const cur = (fields as readonly string[]).indexOf(rawField)

    const isText = el instanceof HTMLInputElement && (el.type === 'text' || el.type === '')
    const atStart = !isText || (el.selectionStart === 0 && el.selectionEnd === 0)
    const atEnd = !isText || (el.selectionEnd === el.value.length)
    const isSelectOrDate = el instanceof HTMLSelectElement
      || (el instanceof HTMLInputElement && el.type === 'date')

    function goPrev() { const p = fields[cur - 1]; if (p) focusField(p as F[number]) }
    function goNext() { const n = fields[cur + 1]; if (n) focusField(n as F[number]) }

    if (e.key === 'ArrowRight' && atEnd) { e.preventDefault(); goNext() }
    else if (e.key === 'ArrowLeft' && atStart) { e.preventDefault(); goPrev() }
    else if (e.key === 'ArrowDown' && !isSelectOrDate) { e.preventDefault(); goNext() }
    else if (e.key === 'ArrowUp' && !isSelectOrDate) { e.preventDefault(); goPrev() }
  }

  return { formRef, onKeyDown, focusField }
}
